import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';
import { NoReaderLayout } from 'slickpay/no-reader/NoReaderLayout.component';
import OnlineLayout from 'slickpay/online/OnlineLayout.component';
import OrderedLayout from 'slickpay/ordered/OrderedLayout.component';

import { beFindReaderV1 } from 'actions/pos/beFindReader.action';
import determinePosReaderId from 'bl/pos/determinePosReaderId.bl';
import Spinner from 'components/generic/Spinner.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import POS_READER_STATUS from 'constants/pos/posReaderStatus.const';
import useAef from 'hooks/useAef.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import logger from 'utils/logger.util';

import PadLock from 'lib/components/PadLock';

import CN from '../no-reader/SlickPayLayout.module.scss';

const CTAG = 'SlickPayLayout';
const L = logger(CTAG);
const SPINNER_COLOR = '#821bb8';
const SPINNER_BACKGROUND_COLOR = '#f9ecff';

const STATUS_TO_LAYOUT = {
  [POS_READER_STATUS.online]: OnlineLayout,
  [POS_READER_STATUS.inactive]: OnlineLayout,
  [POS_READER_STATUS.offline]: OnlineLayout,

  [POS_READER_STATUS.noReader]: NoReaderLayout,
  [POS_READER_STATUS.ordered]: OrderedLayout,
};

const ReaderStatusLayout = ({ status }) => {
  L.debug('ReaderStatusLayout()', 'status:', status);

  const Layout = STATUS_TO_LAYOUT[status];

  if (!Layout) {
    L.warn('ReaderStatusLayout(): For any other not handled', status);
    return <NoReaderLayout />;
  }

  return <Layout />;
};

const SlickPayLayout = ({ salonId, beFindReader }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const {
    value: status,
    error,
    isLoading,
    isLoaded,
  } = useAef(async () => {
    const { readerStatus } = await determinePosReaderId({ salonId, beFindReader });
    L.debug('useAef()', 'readerStatus:', readerStatus);
    return readerStatus;
  }, [salonId, beFindReader]);

  useEffect(() => void window.scrollTo(0, 0), []);

  if (error) {
    L.warn('SlickPayLayout()', error);
  }

  return (
    <div
      data-bem="SlickPayLayout"
      className={cn({
        [CN.layout]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <header data-bem="SlickPayLayout__header" className={CN.header}>
        <SidebarMenu />
        <div className={CN.globalNav}>
          <div className={cn(CN.pageName, CN.clientTags)} />
          <nav className={CN.menuList}>
            <ul className={CN.ulScrollMenu} />
          </nav>
        </div>
        <PadLock inMenu />
      </header>

      {isLoading && <Spinner color={SPINNER_COLOR} message={t('common.loading')} background={SPINNER_BACKGROUND_COLOR} messageColor={SPINNER_COLOR} positionTop="56px" />}
      {isLoaded && <ReaderStatusLayout status={status} />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beFindReader: beFindReaderV1,
    },
    dispatch,
  );

const SlickPayLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(SlickPayLayout);

export default SlickPayLayoutHoc;
